import { Component } from '@theme/component';

/** Native market prices, server ordering, and deliberate batches instead of endless scroll. */
class FrestNewArrivals extends Component {
  #queue = [];
  #seen;
  #next;
  #visited = new Set();
  #failed = false;
  #restoring = false;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.listeners?.abort();
    this.listeners = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.listeners.signal });
    if (this.#seen === undefined) {
      this.#seen = new Set((this.dataset.excludeIds || '').split('|').filter(Boolean));
      this.#next = this.dataset.feedUrl;
    }
    if (this.dataset.ready === 'true') {
      this.#controls();
      return;
    }
    if (this.controller) return;
    const snapshot = this.#backSnapshot();
    if (snapshot) {
      this.#restore(snapshot);
      return;
    }
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }
    this.observer?.disconnect();
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer?.disconnect();
      this.#load();
    }, { rootMargin: '600px 0px' });
    this.observer.observe(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.disconnectedCallback();
    this.#queue = [];
    this.#seen = undefined;
    this.#next = undefined;
    this.#visited.clear();
    this.#failed = false;
    delete this.dataset.ready;
    this.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.#restoring = false;
    this.observer?.disconnect();
    this.listeners?.abort();
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.timeout);
    this.removeAttribute('aria-busy');
    this.#status('');
    this.#controls();
  }

  #onClick = (event) => {
    if (event.target.closest('[data-new-arrivals-grid] a[href]')) this.#saveSnapshot();
    if (this.#restoring || !event.target.closest('[data-new-arrivals-more]')) return;
    this.#load(event.detail === 0);
  };

  #storageKey() {
    return 'frest-arrivals-v2:' + this.dataset.contextKey;
  }

  #saveSnapshot() {
    try {
      sessionStorage.setItem(this.#storageKey(), JSON.stringify({
        count: this.querySelector('[data-new-arrivals-grid]').children.length,
        scroll: window.scrollY,
        saved: Date.now(),
      }));
    } catch { /* Storage can be unavailable; browsing still works. */ }
  }

  #backSnapshot() {
    try {
      if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return null;
      const snapshot = JSON.parse(sessionStorage.getItem(this.#storageKey()));
      if (!snapshot || Date.now() - snapshot.saved > 1800000 || !Number.isInteger(snapshot.count) || snapshot.count < 8 || snapshot.count > 1000 || !Number.isFinite(snapshot.scroll) || snapshot.scroll < 0) return null;
      return snapshot;
    } catch { return null; }
  }

  async #restore(snapshot) {
    this.#restoring = true;
    const generation = this.#generation;
    const grid = this.querySelector('[data-new-arrivals-grid]');
    while (this.isConnected && generation === this.#generation && grid.children.length < snapshot.count) {
      const before = grid.children.length;
      await this.#load(false, Math.min(24, snapshot.count - before));
      if (grid.children.length === before || this.#failed) break;
    }
    if (!this.isConnected || generation !== this.#generation) return;
    this.#restoring = false;
    this.#controls();
    if (grid.children.length >= snapshot.count) {
      window.requestAnimationFrame(() => {
        if (this.isConnected && generation === this.#generation) window.scrollTo({ top: snapshot.scroll, behavior: 'instant' });
      });
    }
  }

  #status(text) {
    const status = this.querySelector('[data-new-arrivals-status]');
    if (!status) return;
    status.textContent = text || '';
    status.hidden = !text;
  }

  #controls(busy = false) {
    busy = busy || this.#restoring;
    const button = this.querySelector('[data-new-arrivals-more]');
    const count = this.querySelector('[data-new-arrivals-count]');
    const shown = this.querySelector('[data-new-arrivals-grid]')?.children.length || 0;
    if (count) {
      count.textContent = shown ? this.dataset.shownText.replace('__COUNT__', shown) : '';
      count.hidden = !shown;
    }
    if (!button) return;
    button.hidden = !busy && !this.#failed && this.#queue.length === 0 && !this.#next;
    button.disabled = busy;
    button.textContent = busy ? this.dataset.loadingText : this.#failed ? this.dataset.retryText : this.dataset.moreText;
  }

  #url(value) {
    const url = new URL(value, window.location.origin);
    const source = new URL(this.dataset.feedUrl, window.location.origin);
    if (url.origin !== source.origin || url.origin !== window.location.origin || url.pathname !== source.pathname) {
      throw new Error('Invalid arrivals destination');
    }
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('section_id', 'frest-new-arrivals-feed');
    const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    return url;
  }

  async #load(keyboard = false, requested = Number(this.dataset.limit) || 8) {
    const grid = this.querySelector('[data-new-arrivals-grid]');
    if (!grid || this.controller) return;
    const batch = Math.min(24, Math.max(1, requested));
    const controller = new AbortController();
    this.controller = controller;
    this.#failed = false;
    this.setAttribute('aria-busy', 'true');
    this.#controls(true);
    this.#status(this.dataset.loadingText);
    this.timeout = setTimeout(() => controller.abort(), 15000);
    try {
      // Bound sparse-page reads per action; keep every eligible card in the queue.
      for (let requests = 0; this.#queue.length < batch && this.#next && requests < 3; requests++) {
        const url = this.#url(this.#next);
        if (this.#visited.has(url.href)) throw new Error('Repeated arrivals page');
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Arrivals request failed: ' + response.status);
        const html = await response.text();
        if (controller.signal.aborted || !this.isConnected || this.controller !== controller) return;
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const feed = fragment.querySelector('[data-new-arrivals-feed]');
        if (!feed) throw new Error('Arrivals fragment unavailable');
        const next = feed.dataset.nextPage ? this.#url(feed.dataset.nextPage).href : null;
        const cards = [];
        const pageIds = new Set();
        for (const card of feed.querySelectorAll('[data-arrival-id]')) {
          const id = card.dataset.arrivalId;
          if (!id || this.#seen.has(id) || pageIds.has(id)) continue;
          pageIds.add(id);
          cards.push(document.importNode(card, true));
        }
        this.#visited.add(url.href);
        for (const id of pageIds) this.#seen.add(id);
        this.#queue.push(...cards);
        this.#next = next;
      }
      const cards = this.#queue.splice(0, batch);
      grid.append(...cards);
      grid.hidden = grid.children.length === 0;
      this.dataset.ready = 'true';
      this.#status(grid.children.length ? '' : this.dataset.emptyText);
      // Preserve pointer users' position; keyboard users reach newly appended products.
      if (keyboard && cards.length) cards[0].querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      if (!this.isConnected || this.controller !== controller) return;
      this.#failed = true;
      this.#status(this.dataset.unavailableText);
      console.debug('Arrivals can be retried; existing artwork stays visible', error);
    } finally {
      if (this.controller === controller) {
        clearTimeout(this.timeout);
        this.controller = null;
        this.removeAttribute('aria-busy');
        this.#controls();
      }
    }
  }
}

if (!customElements.get('frest-new-arrivals')) {
  customElements.define('frest-new-arrivals', FrestNewArrivals);
}
